import { sumMoney } from '@mms/shared';
import { useEffect } from 'react';
import { z } from 'zod';
import type { Row } from '../api/types';
import { amountReq, dateTimeReq, selectReq, toId, toText } from '../forms/zod';
import { useBalance } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import { Segmented } from '../ui/Segmented';
import { FieldRenderer, type CrudConfig, type FieldDef } from './CrudScreen';
import { base, dateCol, noCol } from './transactions';

/**
 * Expense (redesigned 2026-10-16, migration 027): an EXPENSE (money out, Debit) or a REFUND (money back, Credit) with an optional Payee and Bill no, paid
 * from Cash, a Bank or a Dispatcher's account, in the expense's currency. An expense takes the money out of the source (its balance goes down); a refund puts
 * it back (up). Remarks are optional (an automatic text is stored when empty).
 */
const EXPENSE = '2'; // Debit
const REFUND = '1'; // Credit

const expenseSchema = z
  .object({
    typeId: z.string(),
    categoryId: selectReq('Select a category'),
    currencyId: selectReq('Select a currency'),
    amount: amountReq,
    payee: z.string(), billNo: z.string(),
    paidFrom: z.enum(['cash', 'bank', 'dispatcher']),
    bankId: z.string(), supplierId: z.string(),
    transactionDate: dateTimeReq,
    details: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.paidFrom === 'bank' && !v.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Select a bank account' });
    if (v.paidFrom === 'dispatcher' && !v.supplierId) ctx.addIssue({ code: 'custom', path: ['supplierId'], message: 'Select a dispatcher' });
  });

/** A dispatcher lookup row holds a currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

export const expensesConfig: CrudConfig = {
  ...base,
  modalSize: 'xl',
  title: 'Expenses', singular: 'expense', icon: 'receipt', menu: 'expenses', endpoint: '/transactions/expense',
  saveAndNew: true,
  schema: expenseSchema,
  defaults: () => ({
    typeId: EXPENSE, categoryId: '', currencyId: '', amount: '', payee: '', billNo: '', paidFrom: 'cash', bankId: '', supplierId: '',
    transactionDate: nowInput(), details: '',
  }),
  toForm: (r) => ({
    typeId: String(r.typeId), categoryId: String(r.categoryId), currencyId: String(r.currencyId), amount: r.amount, payee: r.payee ?? '', billNo: r.billNo ?? '',
    paidFrom: r.supplierId ? 'dispatcher' : r.troughId === 2 ? 'bank' : 'cash', bankId: r.bankId ? String(r.bankId) : '', supplierId: r.supplierId ? String(r.supplierId) : '',
    transactionDate: toDateTimeInput(r.transactionDate), details: r.details ?? '',
  }),
  toPayload: (v) => ({
    categoryId: toId(v.categoryId), currencyId: toId(v.currencyId), amount: v.amount.trim(), typeId: toId(v.typeId),
    troughId: v.paidFrom === 'cash' ? 1 : v.paidFrom === 'bank' ? 2 : null,
    bankId: v.paidFrom === 'bank' ? toId(v.bankId) ?? null : null, supplierId: v.paidFrom === 'dispatcher' ? toId(v.supplierId) ?? null : null,
    payee: toText(v.payee), billNo: toText(v.billNo), transactionDate: fromDateTimeInput(v.transactionDate), details: toText(v.details),
  }),
  rowLabel: (r) => `${r.transactionNo} (${r.category})`,
  useTable: () => {
    const categories = useLookup('expense-categories');
    const suppliers = useLookup('suppliers');
    // #Ref, Date, Category (with the payee), Amount (with its currency and Expense / Refund), Paid from, Bill no, Remarks
    const columns: Column[] = [
      { ...noCol, header: '#Ref', hideOnMobile: false },
      dateCol,
      { key: 'category', header: 'Category', sortKey: 'category', render: (r) => <><span className="fw-semibold">{r.category}</span>{r.payee ? <small className="text-body-secondary ms-1">{r.payee}</small> : null}</> },
      {
        key: 'amount', header: 'Amount', sortKey: 'amount', align: 'end',
        render: (r) => <><span className={`badge text-bg-${r.typeId === 1 ? 'success' : 'warning'} me-2`}>{r.typeId === 1 ? 'Refund' : 'Expense'}</span><Amount value={r.amount} code={r.currencyCode} /></>,
      },
      {
        key: 'trough', header: 'Paid from', hideOnMobile: true,
        render: (r) => (r.supplierName ? <>Dispatcher<small className="text-body-secondary ms-1">{r.supplierName}</small></> : r.bankName ? <>{r.trough}<small className="text-body-secondary ms-1">{r.bankName}</small></> : <>{r.trough}</>),
      },
      { key: 'billNo', header: 'Bill no', hideOnMobile: true },
      { key: 'details', header: 'Remarks', className: 'text-truncate', hideOnMobile: true, render: (r) => <span title={r.details ?? ''} className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>{r.details ?? ''}</span> },
    ];
    return {
      columns,
      filters: [
        { param: 'categoryId', label: 'Category', options: toOptions(categories.data) },
        { param: 'typeId', label: 'Type', options: [{ value: EXPENSE, label: 'Expense (money out)' }, { value: REFUND, label: 'Refund (money back)' }] },
        { param: 'troughId', label: 'Via', options: [{ value: '1', label: 'Cash' }, { value: '2', label: 'Bank' }] },
        { param: 'supplierId', label: 'Dispatcher', options: toOptions(suppliers.data) },
      ],
    };
  },
  useForm: (form, { readOnly }) => {
    const categories = useLookup('expense-categories');
    const currencies = useLookup('currencies');
    const banks = useLookup('banks');
    const suppliers = useLookup('suppliers');
    const typeId = String(form.watch('typeId') ?? EXPENSE);
    const currencyId = String(form.watch('currencyId') ?? '');
    const amount = String(form.watch('amount') ?? '');
    const paidFrom = String(form.watch('paidFrom') ?? 'cash');
    const bankId = String(form.watch('bankId') ?? '');
    const supplierId = String(form.watch('supplierId') ?? '');
    const isRefund = typeId === REFUND;

    const bankRows = !currencyId ? [] : (banks.data ?? []).filter((b) => String(b.currencyId) === currencyId);
    const supplierRows = !currencyId ? [] : (suppliers.data ?? []).filter((s) => holds(s, currencyId));
    // the source that is not chosen is cleared, and so is one that no longer fits the currency (once its list has loaded: an edit opens before it does)
    useEffect(() => {
      if (paidFrom !== 'bank' && bankId) form.setValue('bankId', '');
      if (paidFrom !== 'dispatcher' && supplierId) form.setValue('supplierId', '');
      if (paidFrom === 'bank' && bankId && !banks.isPending && !bankRows.some((b) => String(b.id) === bankId)) form.setValue('bankId', '');
      if (paidFrom === 'dispatcher' && supplierId && !suppliers.isPending && !supplierRows.some((s) => String(s.id) === supplierId)) form.setValue('supplierId', '');
    }, [paidFrom, currencyId, bankId, supplierId, banks.data, suppliers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    // what saving does to the source: an expense takes it down, a refund puts it back up
    const sourceName = paidFrom === 'cash' ? 'Cash' : paidFrom === 'bank' ? bankRows.find((b) => String(b.id) === bankId)?.name as string | undefined
      : supplierRows.find((s) => String(s.id) === supplierId)?.name as string | undefined;
    const bal = useBalance(
      paidFrom === 'cash' ? 'currencies' : paidFrom === 'bank' ? 'banks' : 'suppliers',
      paidFrom === 'cash' ? currencyId : paidFrom === 'bank' ? bankId : supplierId,
      paidFrom === 'dispatcher' ? currencyId : undefined,
    );
    const after = bal.data && amount ? sumMoney([bal.data.balance, isRefund ? amount : `-${amount}`], 3) : null;

    const field = (def: FieldDef, col = 12) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const currencyOptions = toOptions(currencies.data, (c) => c.code as string);
    const accent = isRefund ? 'success' : 'warning';

    const body = (
      <>
        <Segmented form={form} name="typeId" label="Kind" hideLabel disabled={readOnly}
          options={[
            { value: EXPENSE, label: 'Expense (money out)', variant: 'warning', icon: 'cash-stack' },
            { value: REFUND, label: 'Refund (money back)', variant: 'success', icon: 'arrow-counterclockwise' },
          ]} />
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <section aria-label="Expense" className="rounded border p-3 h-100">
              <div className="fw-semibold mb-2"><i className="bi bi-receipt me-1" aria-hidden />{isRefund ? 'Refund' : 'Expense'}</div>
              <div className="row">
                {field({ name: 'categoryId', label: 'Category', type: 'select', required: true, options: toOptions(categories.data), placeholder: 'Select…' })}
                {field({ name: 'currencyId', label: 'Currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' }, 6)}
                {field({ name: 'amount', label: 'Amount', type: 'money', required: true, placeholder: '0.00' }, 6)}
                {field({ name: 'payee', label: 'Payee (optional)', type: 'text', placeholder: 'Who was paid' }, 6)}
                {field({ name: 'billNo', label: 'Bill no (optional)', type: 'text' }, 6)}
              </div>
            </section>
          </div>
          <div className="col-12 col-md-6">
            <section aria-label={isRefund ? 'Refunded into' : 'Paid from'} className="rounded border p-3 h-100" style={{ borderLeft: `3px solid var(--bs-${accent})` }}>
              <div className={`fw-semibold mb-2 text-${accent}`}>
                <i className={`bi bi-arrow-${isRefund ? 'down-left' : 'up-right'} me-1`} aria-hidden />{isRefund ? 'Refunded into' : 'Paid from'}
              </div>
              <Segmented form={form} name="paidFrom" label={isRefund ? 'Refunded through' : 'Paid through'} required disabled={readOnly}
                options={[
                  { value: 'cash', label: 'Cash', variant: 'secondary', icon: 'cash-coin' },
                  { value: 'bank', label: 'Bank', variant: 'secondary', icon: 'bank' },
                  { value: 'dispatcher', label: 'Dispatcher', variant: 'secondary', icon: 'truck' },
                ]} />
              {paidFrom === 'cash' && <p className="mb-0 text-body-secondary">Cash in hand</p>}
              <div className="row">
                {paidFrom === 'bank' && field({
                  name: 'bankId', label: 'Bank account', type: 'select', required: true, options: toOptions(bankRows),
                  placeholder: currencyId ? 'Select…' : 'Choose the currency first', disabled: !currencyId,
                })}
                {paidFrom === 'dispatcher' && field({
                  name: 'supplierId', label: 'Dispatcher', type: 'select', required: true, options: toOptions(supplierRows),
                  placeholder: currencyId ? 'Select…' : 'Choose the currency first', disabled: !currencyId,
                })}
              </div>
            </section>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">{field({ name: 'transactionDate', label: 'Date & time', type: 'datetime', required: true })}</div>
          <div className="col-md-6">{field({ name: 'details', label: 'Remarks (optional)', type: 'text', placeholder: 'Auto text is used if left empty' })}</div>
        </div>
      </>
    );

    const summary = after && bal.data && sourceName ? (
      <span data-testid="expense-effect">
        {sourceName} balance <Amount value={bal.data.balance} decimals={3} code={bal.data.currencyCode} />
        <i className="bi bi-arrow-right mx-1" aria-hidden />
        <strong className={`text-${accent}`}><Amount value={after} decimals={3} code={bal.data.currencyCode} /></strong>
      </span>
    ) : null;

    return { body, summary, fields: [] };
  },
};
